import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { SectionHeading } from "../common/SectionHeading";
import { Reveal } from "../common/Reveal";
import { api } from "../../lib/api";
import { FALLBACK_PACKS } from "../../lib/data";

export const PartyReady = () => {
    const [packs, setPacks] = useState(FALLBACK_PACKS);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/party-packs")
            .then((res) => { setPacks(res.data || []); setLoaded(true); })
            .catch(() => setLoaded(true));
    }, []);

    if (loaded && packs.length === 0) return null;

    return (
        <section data-testid="party-ready-section" className="bg-pink-soft tent-stripes-light py-24 md:py-32">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <SectionHeading
                    chapter="06"
                    kicker="The Combos"
                    title="Party Ready."
                    copy="Cotton candy + popcorn, packed for the whole crew. Pick a size, pick your flavors."
                    testid="party-ready-heading"
                />
                <div className="grid gap-6 md:grid-cols-3 items-stretch">
                    {packs.map((pack, i) => (
                        <Reveal key={pack.id} delay={i * 0.1} className="h-full">
                            <motion.div
                                whileHover={{ y: -8 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                data-testid={`party-pack-${pack.id}`}
                                className={`h-full rounded-3xl p-8 flex flex-col ${
                                    pack.featured
                                        ? "bg-ink text-white ring-4 ring-pink"
                                        : "bg-white text-ink border border-zinc-200"
                                }`}
                            >
                                {pack.featured && (
                                    <span className="self-start mb-4 rounded-full bg-pink px-4 py-1.5 font-oswald text-[10px] tracking-[0.3em] uppercase text-ink">
                                        Most Popular
                                    </span>
                                )}
                                <h3 className="font-oswald uppercase text-3xl">{pack.name}</h3>
                                <p className={`mt-1 text-sm font-semibold ${pack.featured ? "text-pink" : "text-pink-dark"}`}>{pack.guests}</p>
                                <p className="mt-5 font-oswald text-4xl">
                                    ${pack.price}
                                    <span className={`text-sm font-manrope font-medium ml-2 ${pack.featured ? "text-zinc-400" : "text-zinc-500"}`}>per pack</span>
                                </p>
                                <ul className="mt-6 space-y-3 flex-1">
                                    {pack.contents.map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-sm">
                                            <Check size={16} className={`mt-0.5 shrink-0 ${pack.featured ? "text-pink" : "text-pink-dark"}`} />
                                            <span className={pack.featured ? "text-zinc-300" : "text-zinc-600"}>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                {pack.status === "SOLD_OUT" ? (
                                    <span className="mt-8 inline-flex items-center justify-center rounded-full py-3.5 font-oswald uppercase tracking-widest text-sm bg-zinc-200 text-zinc-400 cursor-not-allowed" data-testid={`party-pack-soldout-${pack.id}`}>
                                        Sold Out
                                    </span>
                                ) : (
                                    <Link
                                        to="/party-packs"
                                        data-testid={`party-pack-cta-${pack.id}`}
                                        className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-oswald uppercase tracking-widest text-sm transition-colors duration-300 active:scale-95 ${
                                            pack.featured
                                                ? "bg-pink text-ink hover:bg-white"
                                                : "bg-ink text-white hover:bg-pink hover:text-ink"
                                        }`}
                                    >
                                        Shop Party Packs <ArrowRight size={15} />
                                    </Link>
                                )}
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
